import { useMutation } from '@tanstack/react-query';
import type { DefaultValues } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { config } from '@/config/config';
import { logger } from '@/config/logger';
import { NotificationId } from '@/modules/api/codegen';
import { useUser } from '@/modules/auth/hooks';
import { actions as messagesActions } from '@/modules/messages';
import { useNotification } from '@/modules/notifications/hooks';

import type { ReviewPrizepoolFormValues } from '../schemas';
import { useReviewPrizepoolStore } from '../services/store';

export interface UseSubmitReviewPrizepoolFormParams {
    onSubmitSuccess: () => void;
}

export const useSubmitReviewPrizepoolForm = ({ onSubmitSuccess }: UseSubmitReviewPrizepoolFormParams) => {
    const dispatch = useDispatch();

    const visited = useReviewPrizepoolStore(state => state.visited);
    const { update } = useNotification(NotificationId.reviewPrizepool);

    const loggedInUser = useUser();
    const passwdInstance = window.location.hostname;

    const { mutate, isPending } = useMutation({
        mutationFn: async (formValues: ReviewPrizepoolFormValues) => {
            const response = await fetch(config.formsparkLinks.reviewPrizepool, {
                method: 'POST',
                body: JSON.stringify({ ...formValues, loggedInUser, passwdInstance }),
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            });

            if (!response.ok) {
                throw new Error(`Formspark rejected the entry with status ${response.status}.`);
            }

            // The entry is already in. A failed ledger write only risks asking again — never the confirmation.
            await update.resolveNotification().catch(logger.error);
        },
        onSuccess: onSubmitSuccess,
        onError: error => {
            dispatch(
                messagesActions.displayErrorMessage({
                    message: { id: 'review-prizepool.form.submit.error' },
                    options: { duration: 5 },
                }),
            );
            logger.error(error);
        },
    });

    // `mutate`, not `mutateAsync`: react-hook-form rethrows out of its submit handler, so a
    // rejecting `mutateAsync` would surface as an unhandled rejection.
    const handleSubmit = (formValues: ReviewPrizepoolFormValues) => {
        mutate(formValues);
    };

    const defaultValues: DefaultValues<ReviewPrizepoolFormValues> = {
        name: '',
        email: '',
        tocAgreement: false,
        trustpilotVisited: visited.trustpilot,
        g2Visited: visited.g2,
    };

    return { handleSubmit, defaultValues, isSubmitting: isPending };
};
